import { useCallback, useEffect, useState } from 'react';
import { ContactAttempt, fetchContactAttempts } from '~/services/contactAttempts';

/**
 * Contact attempts for a campaign request. They load in their own request, apart
 * from the notes, so a failure here used to leave the history showing notes only,
 * as if no one had called (HHLTB-2904). One retry absorbs a transient fault; after
 * that, `attemptsFailed` lets the caller say so instead of showing an empty list.
 */
export const useContactAttempts = (campaignRequestId?: number) => {
    const [contactAttempts, setContactAttempts] = useState<ContactAttempt[]>([]);
    const [loadingAttempts, setLoadingAttempts] = useState(false);
    const [attemptsFailed, setAttemptsFailed] = useState(false);

    const loadContactAttempts = useCallback(async () => {
        if (!campaignRequestId) return;

        setLoadingAttempts(true);
        setAttemptsFailed(false);
        try {
            const attempts = await fetchContactAttempts(campaignRequestId).catch(
                () => fetchContactAttempts(campaignRequestId)
            );
            setContactAttempts(attempts);
        } catch (error) {
            console.error('Error fetching contact attempts:', error);
            setAttemptsFailed(true);
        } finally {
            setLoadingAttempts(false);
        }
    }, [campaignRequestId]);

    useEffect(() => {
        loadContactAttempts();
    }, [loadContactAttempts]);

    return {
        contactAttempts,
        setContactAttempts,
        loadingAttempts,
        attemptsFailed,
        loadContactAttempts,
    };
};
